import { CommonModule } from '@angular/common';
import { Component, signal } from '@angular/core';
import { ReactiveFormsModule, type FormGroup } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from '@infra-adapters/services/auth.service';
import { ToastService } from '@infra-adapters/services/toast.service';
import { RecoverPasswordFormFactory, type IRecoverCodeFormInput, type IRecoverEmailFormInput, type IRecoverNewPasswordFormInput } from '@infra-adapters/forms/recover-password';
import { AuthBaseLayoutComponent } from '@infra-ui/components/auth/auth-base-layout/auth-base-layout.component';
import { COREUI_BUTTON_IMPORTS, COREUI_FORM_IMPORTS } from '@utils/coreui';

type RecoverStep = 'email' | 'code' | 'password';

@Component({
  selector: 'app-recover-password',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    AuthBaseLayoutComponent,
    ...COREUI_FORM_IMPORTS,
    ...COREUI_BUTTON_IMPORTS,
  ],
  templateUrl: './recover-password.component.html',
})
export class RecoverPasswordComponent {
  step = signal<RecoverStep>('email');
  isLoading = signal(false);

  emailForm!: FormGroup<IRecoverEmailFormInput>;
  codeForm!: FormGroup<IRecoverCodeFormInput>;
  passwordForm!: FormGroup<IRecoverNewPasswordFormInput>;

  private recoverToken = '';

  constructor(
    private authService: AuthService,
    private toast: ToastService,
    private formFactory: RecoverPasswordFormFactory,
    private router: Router,
  ) {
    this.emailForm = this.formFactory.generateEmailForm();
    this.codeForm = this.formFactory.generateCodeForm();
    this.passwordForm = this.formFactory.generateNewPasswordForm();
  }

  submitEmail(): void {
    if (this.emailForm.invalid) {
      this.emailForm.markAllAsTouched();
      return;
    }
    this.isLoading.set(true);
    this.authService.sendAdminRecoverMail(this.emailForm.value.email!).subscribe({
      next: () => {
        this.isLoading.set(false);
        this.step.set('code');
      },
      error: (err) => {
        this.isLoading.set(false);
        const msg = err?.error?.message || 'No se pudo enviar el correo. Intenta nuevamente.';
        this.emailForm.get('email')?.setErrors({ backend: msg });
        this.emailForm.get('email')?.markAsTouched();
      },
    });
  }

  submitCode(): void {
    if (this.codeForm.invalid) {
      this.codeForm.markAllAsTouched();
      return;
    }
    this.isLoading.set(true);
    this.authService.validateRecoverCode(this.codeForm.value.code!).subscribe({
      next: (res) => {
        this.recoverToken = res.data.recover_token;
        this.isLoading.set(false);
        this.step.set('password');
      },
      error: (err) => {
        this.isLoading.set(false);
        const msg = err?.error?.message || 'Código inválido o expirado.';
        this.codeForm.get('code')?.setErrors({ backend: msg });
        this.codeForm.get('code')?.markAsTouched();
      },
    });
  }

  submitPassword(): void {
    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      return;
    }
    this.isLoading.set(true);
    this.authService.changePasswordWithRecoverToken(this.recoverToken, this.passwordForm.value.new_password!).subscribe({
      next: () => {
        this.isLoading.set(false);
        this.toast.success('Contraseña actualizada. Ya puedes ingresar con tu nueva contraseña.');
        this.router.navigate(['/login']);
      },
      error: (err) => {
        this.isLoading.set(false);
        const msg = err?.error?.message || 'No se pudo actualizar la contraseña. Intenta nuevamente.';
        this.toast.error(msg);
      },
    });
  }

  getEmailError(field: keyof IRecoverEmailFormInput): string | null {
    const c = this.emailForm.get(field as string);
    if (!c || !c.touched || !c.errors) return null;
    if (c.errors['backend']) return c.errors['backend'];
    if (c.errors['required']) return 'Este campo es obligatorio.';
    if (c.errors['email']) return 'Ingresa un correo electrónico válido.';
    return null;
  }

  getCodeError(): string | null {
    const c = this.codeForm.get('code');
    if (!c || !c.touched || !c.errors) return null;
    if (c.errors['backend']) return c.errors['backend'];
    if (c.errors['required']) return 'Este campo es obligatorio.';
    if (c.errors['pattern']) return 'El código debe ser de 6 dígitos.';
    return null;
  }

  getPasswordError(field: keyof IRecoverNewPasswordFormInput): string | null {
    const c = this.passwordForm.get(field as string);
    if (!c || !c.touched || !c.errors) return null;
    if (c.errors['backend']) return c.errors['backend'];
    if (c.errors['required']) return 'Este campo es obligatorio.';
    if (c.errors['minlength']) return `Debe tener al menos ${c.errors['minlength'].requiredLength} caracteres.`;
    if (c.errors['passwordsMismatch']) return 'Las contraseñas no coinciden.';
    return null;
  }
}
